import { assign } from 'lodash';

import { IntersectionObserver, nativeIntersectionObserverSupported } from '~/lib/IntersectionObserver';

export type tWhenInViewportPromise = Promise<IntersectionObserverEntry>;

export type tWhenInViewportHandle = tWhenInViewportPromise & {
  disconnect(): void;
};

/**
 * Export error message strings for testing
 */
export const enum ErrorMessages {
  InvalidElement = 'Target node must be an element',
  InvalidThreshold = 'Threshold must be a number between 0 and 1 inclusively',
}

/**
 * Exports a boolean indicating whether or not IntersectionObservers
 * are natively supported
 */
export { nativeIntersectionObserverSupported };

/**
 * Basic validation for intersection thresholds which should be a number
 * between 0 and 1 inclusive
 * @param {number} threshold
 * @returns {boolean}
 */
export const isValidThreshold = (threshold: number): boolean =>
  Number(threshold) === threshold && threshold >= 0 && threshold <= 1;

/**
 * @param targetElement - The element to watch for entry into the viewport
 * @param threshold - The threshold at which to trigger the Intersection event.
 *  Must be between 0 and 1 inclusively:
 *    0 - indicates first pixel in viewport
 *    1 - indicates every pixel of element in viewport
 */
export const whenElementInViewport = (targetElement: Element, threshold = 0): tWhenInViewportHandle => {
  let observer: IntersectionObserver;

  const disconnect = () => {
    if (observer) {
      observer.disconnect();
      observer = targetElement = null;
    }
  };

  const promise: tWhenInViewportPromise = new Promise((resolve) => {
    if (!(targetElement && targetElement.nodeType === Node.ELEMENT_NODE)) {
      throw new Error(ErrorMessages.InvalidElement);
    }

    if (!isValidThreshold(threshold)) {
      throw new Error(ErrorMessages.InvalidThreshold);
    }

    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold,
    };

    const callback: IntersectionObserverCallback = (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
        disconnect();
        resolve(entry);
      }
    };

    observer = new IntersectionObserver(callback, options);
    observer.observe(targetElement);
  });

  return assign(promise, {
    disconnect,
  });
};
